import { Injectable } from '@angular/core';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { UserService } from '../user/user.service';
import { getFirebaseBackend } from 'src/app/authUtils';

@Injectable({
	providedIn: 'root',
})
export class PermissionsService {
	private allowedViewIds: number[] = [];
	private permissionsLoaded = new BehaviorSubject<boolean>(false);
	private permissionsLoading = false;
	private lastLoadedAt = 0;

	constructor(private userSrv: UserService) {}

	/**
	 * Obtiene los permisos del usuario logueado y almacena los IDs de las vistas permitidas
	 */
	getUserPermissions() {
		const authUser = getFirebaseBackend()?.getAuthenticatedUser();

		if (!authUser || !authUser.IdUser) {
			console.error('Usuario no autenticado o sin ID de usuario');
			return;
		}

		this.userSrv.getPermissions(authUser.IdUser).subscribe({
			next: (response: any) => {
				// Extraer los IDs de las vistas a las que el usuario tiene permiso
				if (response && response.body && Array.isArray(response.body)) {
					this.allowedViewIds = response.body.filter((permission: any) => permission.isActive == 1).map((permission: any) => permission.view_id);
				} else {
					this.allowedViewIds = [];
				}
				this.lastLoadedAt = Date.now();
				this.permissionsLoaded.next(true);
				this.permissionsLoading = false;
			},
			error: (error) => {
				console.error('Error al obtener permisos del usuario:', error);
				this.allowedViewIds = [];
				this.permissionsLoaded.next(true);
				this.permissionsLoading = false;
			},
		});
	}

	/**
	 * Obtiene los permisos del usuario y retorna un Observable que se completa cuando los permisos están cargados
	 */
	loadUserPermissions(): Observable<boolean> {
		// Si ya están cargados, retornar inmediatamente
		if (this.permissionsLoaded.value) {
			return of(true);
		}

		// Si ya se está cargando, esperar a que termine
		if (this.permissionsLoading) {
			return this.permissionsLoaded.asObservable();
		}

		const authUser = getFirebaseBackend()?.getAuthenticatedUser();

		if (!authUser || !authUser.IdUser) {
			console.error('Usuario no autenticado o sin ID de usuario');
			this.permissionsLoaded.next(true);
			return of(false);
		}

		this.permissionsLoading = true;

		return this.userSrv.getPermissions(authUser.IdUser).pipe(
			map((response: any) => {
				// Extraer los IDs de las vistas a las que el usuario tiene permiso
				if (response && response.body && Array.isArray(response.body)) {
					this.allowedViewIds = response.body.filter((permission: any) => permission.isActive == 1).map((permission: any) => permission.view_id);
				} else {
					this.allowedViewIds = [];
				}
				return true;
			}),
			tap(() => {
				this.lastLoadedAt = Date.now();
				this.permissionsLoaded.next(true);
				this.permissionsLoading = false;
			}),
			catchError((error) => {
				console.error('Error al obtener permisos del usuario:', error);
				this.allowedViewIds = [];
				this.permissionsLoaded.next(true);
				this.permissionsLoading = false;
				return of(false);
			})
		);
	}

	/**
	 * Obtiene la lista de IDs de vistas permitidas para el usuario
	 */
	getAllowedViewIds(): number[] {
		return this.allowedViewIds;
	}

	/**
	 * Verifica si el usuario tiene permiso para acceder a una vista específica
	 */
	hasPermissionForView(viewId: number): boolean {
		return this.allowedViewIds.includes(viewId);
	}

	/**
	 * Verifica si los permisos ya están cargados
	 */
	arePermissionsLoaded(): boolean {
		return this.permissionsLoaded.value;
	}

	/**
	 * Resetea el estado de los permisos (útil para logout o cambio de usuario)
	 */
	resetPermissions(): void {
		this.allowedViewIds = [];
		this.permissionsLoaded.next(false);
		this.permissionsLoading = false;
	}

	/**
	 * Vuelve a pedir los permisos en segundo plano si la última carga tiene más de maxAgeMs.
	 * No limpia los permisos actuales, así que el menú y el guard siguen funcionando mientras
	 * llega la respuesta. Permite que un cambio hecho por un administrador se refleje sin
	 * que el usuario tenga que volver a iniciar sesión.
	 */
	refreshIfStale(maxAgeMs = 60000): void {
		if (!this.permissionsLoaded.value || this.permissionsLoading) return;
		if (Date.now() - this.lastLoadedAt < maxAgeMs) return;
		const authUser = getFirebaseBackend()?.getAuthenticatedUser();
		if (!authUser || !authUser.IdUser) return;

		// Se marca antes de pedir para no lanzar una petición por cada navegación si el API falla.
		this.lastLoadedAt = Date.now();
		this.userSrv.getPermissions(authUser.IdUser).subscribe({
			next: (response: any) => {
				if (response && Array.isArray(response.body)) {
					this.allowedViewIds = response.body.filter((permission: any) => permission.isActive == 1).map((permission: any) => permission.view_id);
				}
			},
			// Si falla se conservan los permisos que ya estaban cargados.
			error: (error) => console.error('Error al refrescar permisos del usuario:', error),
		});
	}

	/**
	 * Limpia los permisos actuales y los recarga desde el servidor.
	 * Debe llamarse justo después del login para asegurar permisos frescos.
	 */
	refreshPermissions(): Observable<boolean> {
		this.resetPermissions();
		return this.loadUserPermissions();
	}
}
